import { useState } from "react";
import type { UseQueryResult } from "@tanstack/react-query";
import { AlertTriangle, Loader2, RefreshCw, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import type { CatalogSweepPlan, SweepNotPreviewed } from "@/types/nvgCatalog";

/**
 * The orphan sweep panel: the plan, and the button that applies it.
 *
 * Kept as one component because the two halves are inseparable by design. The
 * API refuses an apply unless the caller echoes back the `to_reap` from a plan
 * it actually fetched, so the button cannot exist without the plan next to it —
 * and it is handed `plan.to_reap` directly rather than a number the parent
 * tracked separately, so what is confirmed is always what is displayed.
 *
 * Two things this panel deliberately does *not* offer:
 *
 *   1. A grace-days control. The 30-day window is the whole reason the sweep
 *      is safe to run at all; a UI that could set it to 0 would turn one bad
 *      corpus read into an immediate permanent delete. `scripts/sweep_prod.sh`
 *      is still there for the rare case that genuinely needs it.
 *   2. A "sweep everything" shortcut. Every delete goes through the same plan
 *      an operator has read.
 */

/** Nothing is deleted unless rows are actually due. */
function isDestructive(plan: CatalogSweepPlan): boolean {
  return plan.to_reap > 0;
}

/**
 * Whether applying this plan would do anything at all.
 *
 * A plan with no reaps but pending marks or clears is still worth applying —
 * that bookkeeping is what keeps the grace window honest — so "nothing due"
 * and "nothing to do" are different states and the button says which.
 */
function hasWork(plan: CatalogSweepPlan): boolean {
  return plan.to_mark > 0 || plan.to_clear > 0 || plan.to_reap > 0;
}

function applyLabel(plan: CatalogSweepPlan): string {
  if (plan.to_reap > 0) {
    return `Delete ${plan.to_reap} row${plan.to_reap === 1 ? "" : "s"}`;
  }
  if (hasWork(plan)) return "Update tombstones";
  return "Nothing to sweep";
}

function formatDate(iso: string | null): string {
  if (iso === null) return "—";
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleDateString();
}

function Bucket({
  label,
  value,
  hint,
  emphasis,
}: {
  label: string;
  value: number;
  hint: string;
  emphasis?: boolean;
}) {
  return (
    <div className="space-y-1 rounded-md border p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div
        className={
          emphasis && value > 0
            ? "text-2xl font-semibold text-destructive"
            : "text-2xl font-semibold"
        }
      >
        {value}
      </div>
      <div className="text-xs text-muted-foreground">{hint}</div>
    </div>
  );
}

export interface CatalogSweepCardProps {
  sweep: UseQueryResult<CatalogSweepPlan, Error>;
  /** Called with the plan's own reap count — never a separately tracked one. */
  onApply: (confirmReap: number) => void;
  /** True while any catalog run is in flight; the apply must not stack. */
  busy: boolean;
  /** Set when the server refused a confirmation that no longer matches. */
  notPreviewed: SweepNotPreviewed | null;
  onDismissNotPreviewed: () => void;
}

export function CatalogSweepCard({
  sweep,
  onApply,
  busy,
  notPreviewed,
  onDismissNotPreviewed,
}: CatalogSweepCardProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const plan = sweep.data;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle>Orphaned</CardTitle>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => void sweep.refetch()}
          disabled={sweep.isFetching}
        >
          {sweep.isFetching ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {sweep.isError ? (
          <p className="text-sm text-destructive">{sweep.error.message}</p>
        ) : plan === undefined ? (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              Stored rows the live corpus no longer resolves to. Not a render
              backlog — nothing will ever request these again, and no amount of
              rendering removes them. Measured on the box; the check is not
              free.
            </p>
            {sweep.isFetching ? (
              <Skeleton className="h-9 w-36" />
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => void sweep.refetch()}
              >
                Check for orphans
              </Button>
            )}
          </div>
        ) : (
          <>
            <div className="space-y-1">
              <div className="text-sm text-muted-foreground">
                Rows the corpus no longer resolves to
              </div>
              <div className="text-3xl font-semibold">{plan.orphaned}</div>
              <div className="text-xs text-muted-foreground">
                of {plan.total_tube_rows} stored tube rows, against{" "}
                {plan.valid_entry_key_count} reachable keys
                {plan.cached ? " (cached)" : ""}
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <Bucket
                label="To tombstone"
                value={plan.to_mark}
                hint="clock starts on apply"
              />
              <Bucket
                label="In the window"
                value={plan.waiting}
                hint={
                  plan.next_due_at === null
                    ? `${plan.grace_days}-day grace`
                    : `next due ${formatDate(plan.next_due_at)}`
                }
              />
              <Bucket
                label="Due for deletion"
                value={plan.to_reap}
                hint="deleted on apply"
                emphasis
              />
            </div>

            {plan.to_clear > 0 && (
              <p className="text-xs text-muted-foreground">
                {plan.to_clear} tombstoned row(s) resolve again and will have
                their clock cleared. That is the flicker-healing half of the
                sweep — a transient scrape gap, already recovered.
              </p>
            )}

            {plan.unattended_would_refuse_reap && (
              <div className="flex gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3">
                <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" />
                <p className="text-xs">
                  Tonight&rsquo;s scheduled sweep will <strong>not</strong>{" "}
                  delete these: {(plan.reap_fraction * 100).toFixed(1)}% of all
                  tube rows are due, above its{" "}
                  {(plan.unattended_max_delete_fraction * 100).toFixed(0)}%
                  unattended limit. From the box a real delisting wave and a
                  scrape that lost part of the corpus look identical — this
                  button is the human override for exactly that call.
                </p>
              </div>
            )}

            {plan.to_reap === 0 && plan.to_mark > 0 && (
              <p className="text-xs text-muted-foreground">
                Nothing is due today. Applying starts the {plan.grace_days}-day
                clock on {plan.to_mark} row(s); they become deletable on a later
                run, not this one.
              </p>
            )}

            {notPreviewed !== null && (
              <div className="flex items-start gap-3 rounded-md border border-amber-500/40 bg-amber-500/10 p-4">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
                <div className="space-y-2">
                  <p className="text-sm">
                    {notPreviewed.expected === null
                      ? "The plan you confirmed has expired. Nothing was deleted."
                      : `The plan moved: ${notPreviewed.expected} row(s) are now due, not the ${notPreviewed.confirmed} you confirmed. Nothing was deleted.`}
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      onDismissNotPreviewed();
                      void sweep.refetch();
                    }}
                  >
                    Re-check the plan
                  </Button>
                </div>
              </div>
            )}

            <div className="flex items-center gap-3">
              <Button
                variant={isDestructive(plan) ? "destructive" : "secondary"}
                onClick={() => setConfirmOpen(true)}
                disabled={busy || !hasWork(plan)}
              >
                {isDestructive(plan) && <Trash2 className="mr-2 h-4 w-4" />}
                {applyLabel(plan)}
              </Button>
              <Badge variant="secondary">on the API box</Badge>
            </div>
          </>
        )}
      </CardContent>

      {plan !== undefined && (
        <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {isDestructive(plan)
                  ? `Permanently delete ${plan.to_reap} row(s)?`
                  : "Apply the sweep's bookkeeping?"}
              </DialogTitle>
              <DialogDescription asChild>
                <div className="space-y-3">
                  {isDestructive(plan) ? (
                    <p>
                      This deletes {plan.to_reap} row(s) from{" "}
                      <code>simulation_assets</code> and their objects from S3.{" "}
                      <strong>There is no rollback</strong> — the only way back
                      is to re-render and re-upload. Every row here has been
                      unreachable from the live corpus for the full{" "}
                      {plan.grace_days}-day window.
                    </p>
                  ) : (
                    <p>
                      Nothing is due for deletion, so nothing will be deleted.
                      This starts the clock on {plan.to_mark} newly orphaned
                      row(s) and clears it on {plan.to_clear} revived one(s).
                    </p>
                  )}
                  {plan.sample_reap.length > 0 && (
                    <div className="space-y-1">
                      <div className="text-xs font-medium">
                        A sample of what goes:
                      </div>
                      <pre className="max-h-32 overflow-auto rounded bg-muted p-2 text-xs">
                        {plan.sample_reap.join("\n")}
                      </pre>
                    </div>
                  )}
                  <p className="text-xs">
                    Confirmed against the plan shown above. If it has moved
                    since — another operator, an upload, or the nightly sweep —
                    the server refuses rather than deleting a different set.
                  </p>
                </div>
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmOpen(false)}>
                Cancel
              </Button>
              <Button
                variant={isDestructive(plan) ? "destructive" : "default"}
                onClick={() => {
                  setConfirmOpen(false);
                  // The plan's own number, never one tracked alongside it.
                  onApply(plan.to_reap);
                }}
              >
                {applyLabel(plan)}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Card>
  );
}
